import React, { createContext, useState, useContext } from 'react';
import GlobalModal from './GlobalModal';

interface ModalState {
  isOpen: boolean;
  title?: string;
  modalComponent?: React.ReactElement; // To hold the component
}

interface ModalContextProps {
  modal: ModalState;
  openModal: (title: string, component: React.ReactElement) => void; // Accepts a React component
  closeModal: () => void;
}

interface ModalProviderProps {
  children: React.ReactNode;
}

const createModalProvider = () => {
  const ModalContext = createContext<ModalContextProps | null>(null);

  const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
      throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
  };

  const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
    const [modal, setModal] = useState<ModalState>({ isOpen: false });

    const openModal = (title: string, component: React.ReactElement) => {
      setModal({ isOpen: true, modalComponent: component, title });
    };

    const closeModal = () => {
      setModal({ isOpen: false, modalComponent: undefined });
    };

    return (
      <ModalContext.Provider value={{ modal, openModal, closeModal }}>
        {children}
        <GlobalModal title={modal.title ?? ''} isOpen={modal.isOpen} closeModal={closeModal}>
          {modal.modalComponent}
        </GlobalModal>
      </ModalContext.Provider>
    );
  };

  return { ModalProvider, useModal };
};

export const { ModalProvider, useModal } = createModalProvider();
